/**
 * AURA State Orchestrator
 *
 * Zustand store managing all field state: camera, mode, selection, time, UI.
 * Designed for high-frequency updates from the render loop without triggering
 * unnecessary re-renders in the thin UI chrome layer.
 */

import { create } from 'zustand';

export type FlyWheelMode = 'sense' | 'validate' | 'field' | 'create' | 'activate' | 'learn' | 'evolve';
export type Trend = 'rising' | 'stable' | 'fading' | 'emerging';
export type DepthLevel = 'overview' | 'zone' | 'cluster' | 'node';
export type LearnAxis = 'validation' | 'momentum' | 'connectivity' | 'depth' | 'readiness' | 'causality';

export interface ClusterContext {
  id: string;
  name: string;
  category: string;
  signalStrength: number;
  trend: Trend;
  nodeIndex: number;
}

/** Zone info displayed in the territory panel */
export interface ZoneContext {
  name: string;
  color: string;                     // CSS color string
  clusters: {
    name: string;
    weight: number;                  // 0–1 authored weight
    trend: Trend;
    nodeIndex: number;               // index into SyntheticField.nodes
  }[];
}

/** Cluster detail displayed when focused */
export interface ClusterDetail {
  name: string;
  category: string;
  weight: number;
  trend: Trend;
  color: string;
  nodeIndex: number;
  subSignals: { name: string; intensity: number; trend: Trend }[];
  connections: { name: string; category: string; weight: number }[];
}

export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
  velocity: [number, number, number];
  positionStack: Array<{
    position: [number, number, number];
    target: [number, number, number];
  }>;
}

export interface AuraStore {
  // Camera
  camera: CameraState;
  setCameraPosition: (pos: [number, number, number]) => void;
  setCameraTarget: (target: [number, number, number]) => void;
  pushCameraState: () => void;
  popCameraState: () => [number, number, number] | null;
  clearCameraStack: () => void;

  // Depth navigation
  depthLevel: DepthLevel;
  activeZone: ZoneContext | null;
  activeCluster: ClusterDetail | null;
  setDepth: (level: DepthLevel) => void;
  setActiveZone: (zone: ZoneContext | null) => void;
  setActiveCluster: (cluster: ClusterDetail | null) => void;

  // Mode
  mode: FlyWheelMode;
  modeTransitionProgress: number;
  setMode: (mode: FlyWheelMode) => void;
  setModeTransitionProgress: (p: number) => void;

  // Selection
  hoveredNodeIndex: number;
  selectedNodeIndex: number;
  contextData: ClusterContext | null;
  setHoveredNode: (index: number) => void;
  setSelectedNode: (index: number, context?: ClusterContext) => void;
  clearSelection: () => void;

  // Active field cluster (highlights the live cluster in the 3D field)
  activeFieldClusterIndex: number | null;
  setActiveFieldCluster: (nodeIndex: number | null) => void;

  // Time
  currentTime: number;
  timeRange: [number, number];
  scrubbing: boolean;
  setCurrentTime: (t: number) => void;
  setScrubbing: (s: boolean) => void;

  // Learn
  learnAxis: LearnAxis;
  setLearnAxis: (axis: LearnAxis) => void;

  // UI
  entryCompleted: boolean;
  entryProgress: number;
  setEntryProgress: (p: number) => void;
  setEntryCompleted: () => void;
}

export const useAuraStore = create<AuraStore>((set, get) => ({
  // Camera
  camera: {
    position: [0, 0, 800],
    target: [0, 0, 0],
    velocity: [0, 0, 0],
    positionStack: [],
  },
  setCameraPosition: (pos) =>
    set((state) => ({
      camera: { ...state.camera, position: pos },
    })),
  setCameraTarget: (target) =>
    set((state) => ({
      camera: { ...state.camera, target },
    })),
  pushCameraState: () =>
    set((state) => ({
      camera: {
        ...state.camera,
        positionStack: [
          ...state.camera.positionStack,
          { position: [...state.camera.position] as [number, number, number], target: [...state.camera.target] as [number, number, number] },
        ],
      },
    })),
  popCameraState: () => {
    const stack = get().camera.positionStack;
    if (stack.length === 0) return null;
    const prev = stack[stack.length - 1];
    set((state) => ({
      camera: {
        ...state.camera,
        positionStack: state.camera.positionStack.slice(0, -1),
      },
    }));
    return prev.position;
  },
  clearCameraStack: () =>
    set((state) => ({
      camera: { ...state.camera, positionStack: [] },
    })),

  // Depth navigation
  depthLevel: 'overview',
  activeZone: null,
  activeCluster: null,
  setDepth: (level) => set({ depthLevel: level }),
  setActiveZone: (zone) => set({ activeZone: zone }),
  setActiveCluster: (cluster) => set({ activeCluster: cluster }),

  // Active field cluster
  activeFieldClusterIndex: null,
  setActiveFieldCluster: (nodeIndex) => set({ activeFieldClusterIndex: nodeIndex }),

  // Mode
  mode: 'sense',
  modeTransitionProgress: 1,
  setMode: (mode) =>
    set({ mode, modeTransitionProgress: 0 }),
  setModeTransitionProgress: (p) =>
    set({ modeTransitionProgress: p }),

  // Selection
  hoveredNodeIndex: -1,
  selectedNodeIndex: -1,
  contextData: null,
  setHoveredNode: (index) => set({ hoveredNodeIndex: index }),
  setSelectedNode: (index, context) =>
    set({ selectedNodeIndex: index, contextData: context ?? null }),
  clearSelection: () =>
    set({ selectedNodeIndex: -1, contextData: null }),

  // Time
  currentTime: Date.now(),
  timeRange: [Date.now() - 90 * 24 * 60 * 60 * 1000, Date.now()],
  scrubbing: false,
  setCurrentTime: (t) => set({ currentTime: t }),
  setScrubbing: (s) => set({ scrubbing: s }),

  // Learn
  learnAxis: 'validation',
  setLearnAxis: (axis) => set({ learnAxis: axis }),

  // UI
  entryCompleted: false,
  entryProgress: 0,
  setEntryProgress: (p) => set({ entryProgress: p }),
  setEntryCompleted: () => set({ entryCompleted: true, entryProgress: 1 }),
}));
